import { create } from 'zustand';
import { registerStoreReset } from './storeResetUtils';
import * as LeavePolicyService from '@/services/HrService/LeavePolicyService';

interface HolidayConfig {
  max_optional_holidays: number;
  max_compulsory_holidays: number;
}

interface CarryForwardRule {
  id: number;
  leave_name: string;
  total_leaves: number;
  carry_forward_enabled: number;
  max_carry_forward_days: number;
  carry_forward_expiry_months: number | null;
  is_pro_rata_applicable: number;
}

interface CarryForwardLogEntry {
  id: number;
  user_id: string;
  leave_type_id: number;
  leave_name: string;
  employee_name: string;
  from_fy_year: number;
  to_fy_year: number;
  remaining_leaves: number;
  carried_forward_amount: number;
  expiry_date: string | null;
  status: 'active' | 'expired' | 'used';
  processed_at: string;
}

interface ProRataSettings {
  enabled: boolean;
  rounding_method: 'round' | 'ceil' | 'floor';
}

interface YearlyQuota {
  leave_type_id: number;
  leave_name: string;
  default_quota: number;
  yearly_quota: number | null;
  effective_quota: number;
}

interface ApiError {
  message: string;
  statusCode?: number;
}

interface LeavePolicyState {
  holidayConfig: HolidayConfig | null;
  carryForwardRules: CarryForwardRule[];
  carryForwardLog: CarryForwardLogEntry[];
  proRataSettings: ProRataSettings | null;
  availableFinancialYears: string[];
  yearlyQuotas: YearlyQuota[];
  loading: boolean;
  error: string | null;
  activeTab: number;

  fetchAllSettings: () => Promise<void>;
  fetchHolidayConfig: (fyYear?: number) => Promise<void>;
  updateHolidayConfig: (data: Partial<HolidayConfig> & { fy_year?: number }) => Promise<{ success: boolean; error?: string }>;
  fetchCarryForwardRules: () => Promise<void>;
  updateCarryForwardRule: (leaveTypeId: number, data: Partial<CarryForwardRule>) => Promise<{ success: boolean; error?: string }>;
  triggerCarryForward: (fromFyYear: number) => Promise<{ success: boolean; data?: any; error?: ApiError }>;
  fetchCarryForwardLog: (userId?: string, fyYear?: number) => Promise<void>;
  fetchProRataSettings: () => Promise<void>;
  updateProRataSettings: (data: Partial<ProRataSettings>) => Promise<{ success: boolean; error?: string }>;
  fetchAvailableFinancialYears: () => Promise<void>;
  initializeFinancialYear: (fyYear: string) => Promise<{ success: boolean; data?: any; error?: string }>;
  fetchYearlyQuotas: (fyYear: string) => Promise<void>;
  updateYearlyQuota: (leaveTypeId: number, fyYear: string, totalLeaves: number) => Promise<{ success: boolean; error?: string }>;
  setActiveTab: (tab: number) => void;
  resetStore: () => void;
}

const initialState = {
  holidayConfig: null,
  carryForwardRules: [],
  carryForwardLog: [],
  proRataSettings: null,
  availableFinancialYears: [],
  yearlyQuotas: [],
  loading: false,
  error: null,
  activeTab: 0,
};

const useLeavePolicyStore = create<LeavePolicyState>((set) => ({
  ...initialState,

  fetchAllSettings: async () => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getAllPolicySettings();
      if (response.success && response.data) {
        set({
          holidayConfig: response.data.holidayConfig,
          carryForwardRules: response.data.carryForwardRules || [],
          proRataSettings: response.data.proRataSettings,
          loading: false,
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch settings', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchHolidayConfig: async (fyYear) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getHolidayConfig(fyYear);
      if (response.success) {
        set({ holidayConfig: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch holiday config', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  updateHolidayConfig: async (data) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.updateHolidayConfig(data);
      if (response.success) {
        set({ holidayConfig: response.data, loading: false });
        return { success: true };
      } else {
        set({ error: response.error?.message, loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  fetchCarryForwardRules: async () => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getCarryForwardRules();
      if (response.success) {
        set({ carryForwardRules: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message, loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  updateCarryForwardRule: async (leaveTypeId, data) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.updateCarryForwardRule(leaveTypeId, data);
      if (response.success) {
        set((state) => ({
          carryForwardRules: state.carryForwardRules.map((rule) =>
            rule.id === leaveTypeId ? { ...rule, ...response.data } : rule
          ),
          loading: false,
        }));
        return { success: true };
      } else {
        set({ error: response.error?.message, loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  triggerCarryForward: async (fromFyYear) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.processCarryForward(fromFyYear);
      set({ loading: false });
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message });
        return { success: false, error: response.error };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: { message: error.message } };
    }
  },

  fetchCarryForwardLog: async (userId, fyYear) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getCarryForwardLog(userId, fyYear);
      if (response.success) {
        set({ carryForwardLog: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message, loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchProRataSettings: async () => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getProRataSettings();
      if (response.success) {
        set({ proRataSettings: response.data, loading: false });
      } else {
        set({ error: response.error?.message, loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  updateProRataSettings: async (data) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.updateProRataSettings(data);
      if (response.success) {
        set({ proRataSettings: response.data, loading: false });
        return { success: true };
      } else {
        set({ error: response.error?.message, loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  fetchAvailableFinancialYears: async () => {
    try {
      const response = await LeavePolicyService.getAvailableFinancialYears();
      if (response.success) {
        set({ availableFinancialYears: response.data || [] });
      } else {
        set({ error: response.error?.message });
      }
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  initializeFinancialYear: async (fyYear) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.initializeFinancialYear(fyYear);
      set({ loading: false });
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        const errMsg = response.error?.message || 'Failed to initialize financial year';
        set({ error: errMsg });
        return { success: false, error: errMsg };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  fetchYearlyQuotas: async (fyYear) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.getYearlyQuotas(fyYear);
      if (response.success) {
        set({ yearlyQuotas: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message, loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  updateYearlyQuota: async (leaveTypeId, fyYear, totalLeaves) => {
    try {
      set({ loading: true, error: null });
      const response = await LeavePolicyService.updateYearlyQuota(leaveTypeId, fyYear, totalLeaves);
      if (response.success) {
        // Refresh quotas after update
        const quotasResponse = await LeavePolicyService.getYearlyQuotas(fyYear);
        if (quotasResponse.success) {
          set({ yearlyQuotas: quotasResponse.data || [], loading: false });
        }
        return { success: true };
      } else {
        set({ error: response.error?.message, loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  resetStore: () => set(initialState),
}));

// Register for auto-reset on logout
registerStoreReset('leavePolicyStore', () => useLeavePolicyStore.getState().resetStore());

export default useLeavePolicyStore;
